import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AesEncryptDecryptService {
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();
  private key = environment.AG_GRID_K;
  private iv = new TextEncoder().encode(environment.AG_GRID_K_IV);

  /**
   * Imports a raw AES key from a string.
   *
   *
   * @param {string} key
   * @return {*}  {Promise<CryptoKey>}
   * @memberof AesEncryptDecryptService
   */
  public async importKey(key: string): Promise<CryptoKey> {
    const rawKey = this.encoder.encode(key);
    return crypto.subtle.importKey('raw', rawKey, { name: 'AES-CBC' }, false, [
      'encrypt',
      'decrypt',
    ]);
  }

  /**
   * Encrypts a secretData string using AES encryption.
   *
   * @param {string} secretData
   * @param {string} key
   * @param {Uint8Array} iv
   * @return {*}  {Promise<string>}
   * @memberof AesEncryptDecryptService
   */
  public async encryptAES(secretData: string): Promise<string> {
    const cryptoKey = await this.importKey(this.key);
    const data = this.encoder.encode(secretData);
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv: this.iv as BufferSource },
      cryptoKey,
      data,
    );
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  /**
   * Decrypts a secret data string using AES decryption.
   *
   * @param {string} secretData
   * @param {string} key
   * @param {Uint8Array} iv
   * @return {*}  {Promise<string>}
   * @memberof AesEncryptDecryptService
   */
  public async decryptAES(secretData: string): Promise<string> {
    const cryptoKey = await this.importKey(this.key);
    const encryptedData = Uint8Array.from(atob(secretData), (c) =>
      c.charCodeAt(0),
    );
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv: this.iv as BufferSource },
      cryptoKey,
      encryptedData,
    );
    return this.decoder.decode(decrypted);
  }
}
